import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useTheme } from 'styled-components';
import Editor from '@monaco-editor/react';
import { Header, Button, Icon, Toggle } from '@salecycle/ui-component-library';
import {
  ConfigScrollContainer,
  ConfigHeaderWrapper,
  SnapshotViewContent,
  SnapshotEditorContainer,
  SnapshotHeaderButtons,
} from './style';
import { useRcsTemplateEditorDispatch, useRcsTemplateEditorState } from '../context/useRcsTemplateEditorContext';
import { useRandomSnapshotSelection } from 'sections/design/template-management/hooks/useRandomSnapshotSelection';
import useNotifications from 'core/notifications/useNotifications';
import { RcsRandomiseSnapshotModal } from './modals/RcsRandomiseSnapshotModal';
import { RcsImportSnapshotModal } from './modals/RcsImportSnapshotModal';

type MonacoTheme = 'light' | 'vs-dark';

const DEFAULT_JSON_VALUE = '{}';

function snapshotToEditorJson(snapshot: { compilationContext?: unknown }): string {
  const payload =
    snapshot.compilationContext !== undefined ? { compilationContext: snapshot.compilationContext } : snapshot;
  return JSON.stringify(payload, null, 2);
}

export const RcsSnapshotEditorView: React.FC = () => {
  const { t } = useTranslation(['designs']);
  const theme = useTheme();
  const { siteId } = useParams<{ siteId: string }>();
  const { editorSettings, snapshotJson, skipRandomiseSnapshotModal } = useRcsTemplateEditorState();
  const dispatch = useRcsTemplateEditorDispatch();
  const { showNotification } = useNotifications();
  const [randomiseModalOpen, setRandomiseModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);

  const randomSnapshotQuery = useRandomSnapshotSelection(siteId, 'offsite');
  const randomiseRequestedRef = useRef(false);

  const runRandomisation = useCallback(() => {
    const cachedData = randomSnapshotQuery.data;
    if (cachedData && cachedData.length > 0) {
      const chosen = cachedData[Math.floor(Math.random() * cachedData.length)];
      dispatch({ type: 'SET_SNAPSHOT_JSON', payload: { snapshotJson: snapshotToEditorJson(chosen) } });
      return;
    }

    randomiseRequestedRef.current = true;
    randomSnapshotQuery.refetch();
  }, [randomSnapshotQuery, dispatch]);

  useEffect(() => {
    if (!randomiseRequestedRef.current || randomSnapshotQuery.isIdle || randomSnapshotQuery.isLoading) {
      return;
    }
    randomiseRequestedRef.current = false;

    if (!randomSnapshotQuery.isSuccess) {
      showNotification(t('designs:rcsTemplateEditor.snapshotTab.randomiseModal.error'), 'error');
      return;
    }

    const data = randomSnapshotQuery.data;
    if (!data || data.length === 0) {
      showNotification(t('designs:rcsTemplateEditor.snapshotTab.randomiseModal.noSnapshots'), 'error');
      return;
    }

    const chosen = data[Math.floor(Math.random() * data.length)];
    dispatch({ type: 'SET_SNAPSHOT_JSON', payload: { snapshotJson: snapshotToEditorJson(chosen) } });
  }, [
    dispatch,
    showNotification,
    t,
    randomSnapshotQuery.isIdle,
    randomSnapshotQuery.isLoading,
    randomSnapshotQuery.isSuccess,
    randomSnapshotQuery.data,
  ]);

  const handleRandomiseClick = useCallback(() => {
    if (skipRandomiseSnapshotModal) {
      runRandomisation();
    } else {
      setRandomiseModalOpen(true);
    }
  }, [skipRandomiseSnapshotModal, runRandomisation]);

  const handleRandomiseModalContinue = useCallback(
    (dontShowAgain: boolean) => {
      if (dontShowAgain) {
        dispatch({ type: 'SET_SKIP_RANDOMISE_SNAPSHOT_MODAL', payload: { skipRandomiseSnapshotModal: true } });
      }
      runRandomisation();
    },
    [dispatch, runRandomisation],
  );

  const handleRandomiseModalClose = useCallback(() => {
    setRandomiseModalOpen(false);
  }, []);

  const isDarkMode = editorSettings.theme === 'dark';
  const editorTheme: MonacoTheme = isDarkMode ? 'vs-dark' : 'light';
  const blueIconColor = theme?.vars?.newColor?.blue1 ?? theme?.designToken?.primary700;

  const handleDarkModeChange = (checked: boolean) => {
    dispatch({ type: 'SET_EDITOR_SETTINGS', payload: { theme: checked ? 'dark' : 'light' } });
  };

  const handleEditorChange = (value: string | undefined) => {
    dispatch({ type: 'SET_SNAPSHOT_JSON', payload: { snapshotJson: value ?? DEFAULT_JSON_VALUE } });
  };

  return (
    <>
      <ConfigScrollContainer data-testid="RcsSnapshotEditorView">
        <SnapshotViewContent>
          <ConfigHeaderWrapper>
            <Header
              heading={t('designs:rcsTemplateEditor.snapshotTab.header')}
              description={t('designs:rcsTemplateEditor.snapshotTab.description')}
            >
              <SnapshotHeaderButtons>
                <Button
                  variation="secondary"
                  ariaLabel={t('designs:rcsTemplateEditor.snapshotTab.import')}
                  leftSideIcon={<Icon icon="cloud-upload" />}
                  iconColor={blueIconColor}
                  onClick={() => setImportModalOpen(true)}
                >
                  {t('designs:rcsTemplateEditor.snapshotTab.import')}
                </Button>
                <Button
                  variation="secondary"
                  ariaLabel={t('designs:rcsTemplateEditor.snapshotTab.randomise')}
                  leftSideIcon={<Icon icon="exchange" />}
                  iconColor={blueIconColor}
                  onClick={handleRandomiseClick}
                >
                  {t('designs:rcsTemplateEditor.snapshotTab.randomise')}
                </Button>
              </SnapshotHeaderButtons>
            </Header>
          </ConfigHeaderWrapper>

          <Toggle
            ariaLabel={t('designs:rcsTemplateEditor.snapshotTab.darkMode')}
            label={t('designs:rcsTemplateEditor.snapshotTab.darkMode')}
            checked={isDarkMode}
            onChange={handleDarkModeChange}
            margin={true}
            testId="SnapshotJsonDarkModeToggle"
          />

          <SnapshotEditorContainer>
            <Editor
              options={{
                minimap: { enabled: false },
                automaticLayout: true,
                wordWrap: 'on',
                ariaLabel: 'Snapshot JSON editor',
              }}
              theme={editorTheme}
              value={snapshotJson}
              onChange={handleEditorChange}
              language="json"
            />
          </SnapshotEditorContainer>
        </SnapshotViewContent>
      </ConfigScrollContainer>

      <RcsRandomiseSnapshotModal
        isOpen={randomiseModalOpen}
        onClose={handleRandomiseModalClose}
        onContinue={handleRandomiseModalContinue}
      />
      <RcsImportSnapshotModal
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onSuccess={snapshot => {
          dispatch({
            type: 'SET_SNAPSHOT_JSON',
            payload: { snapshotJson: snapshotToEditorJson(snapshot) },
          });
          setImportModalOpen(false);
        }}
      />
    </>
  );
};
